import { LabelField } from 'components/form/field/LabelField';
import { isNil, round } from 'lodash';
import { ReactElement, useCallback, useMemo, useState } from 'react';
import i18n from 'i18n';
import { Modal } from 'components/Modal/Modal';
import { InputGroupField } from 'components/form/field/InputGroupField';
import { CustomField } from 'components/form/field/CustomField';
import styles from './modal.module.scss';
import { DefaultRtbCampaignManager } from 'core/rtbCampaign/RtbCampaignManager';
import { Trans } from 'react-i18next';
import { DAILY_BUDGET_STATE } from 'containers/RtbCampaigns/RtbCampaignSetupFlow/RtbCampaignForm/RtbCampaignBasicFormModel';
import { AlertDialog } from 'components/AlertDialog';
import { renderOverBudgetWording } from 'containers/L2Objects/FormHintRenderFunctions';
import { L1Object } from 'core/l1Object/L1Object';
import { Order } from 'core/order/Order';
import { CampaignState } from 'core/rtbCampaign/RtbCampaign';
import { useCallAPI } from 'hooks/useCallAPI';
import { LoadingIndicator } from 'components/LoadingIndicator';
import { formatPriceWithCurrency } from 'helper/CurrencyHelper';
import { validateCampaignBudget, validateCampaignDailyBudget } from 'utils/CampaignValidators';

const campaignManager = new DefaultRtbCampaignManager();

export const renderModifyBudgetModal = (
  campaign: {
    id: number;
    state: CampaignState;
    budget: number;
    spents: number;
    expectedSpent: number;
    startDate: string;
    endDate: string;
    dailyTargetBudget: number | undefined;
  },
  order: Order,
  l1Object: L1Object,
  onClose: (needRefreshList: boolean) => void
) => {
  return (
    <ModifyBudget
      campaign={campaign}
      order={order}
      l1Object={l1Object}
      onClose={onClose}
    />
  );
};

export const ModifyBudget: React.FC<{
  campaign: {
    id: number;
    state: CampaignState;
    budget: number;
    spents: number;
    expectedSpent: number;
    startDate: string;
    endDate: string;
    dailyTargetBudget?: number;
  };
  order: Order;
  l1Object: L1Object;
  onClose: (needRefreshList: boolean) => void;
}> = ({ campaign, order, l1Object, onClose }) => {
  const {
    id,
    state,
    budget,
    spents,
    expectedSpent,
    startDate,
    endDate,
    dailyTargetBudget
  } = campaign;
  const [needShowDailyBudgetAlert, setNeedShowDailyBudgetAlert] = useState<boolean>(false);
  const [newBudget, setNewBudget] = useState<number>(budget);
  const [newDailyBudget, setNewDailyBudget] = useState<number | undefined>(
    dailyTargetBudget
  );
  const [showFinalCheckModal, setShowFinalCheckModal] = useState<boolean>(false);

  const newBudgetError = useMemo(() => {
    if (budget === newBudget) {
      return undefined;
    }
    const totalBudget = l1Object.budgetBalance;
    const remainBudget = totalBudget - newBudget + budget;
    if (remainBudget < 0) {
      return renderOverBudgetWording(order.currency, totalBudget);
    }

    return validateCampaignBudget({
      budget: newBudget,
      spents,
      expectedSpent,
      campaignBudgetMinimum: order.campaignConstraint.campaignBudgetMinimum,
      currency: order.currency,
      state
    });
  }, [
    state,
    newBudget,
    budget,
    spents,
    expectedSpent,
    order.currency,
    order.campaignConstraint.campaignBudgetMinimum,
    l1Object.budgetBalance
  ]);

  const newDailyBudgetError = useMemo(() => {
    if (dailyTargetBudget === newDailyBudget) {
      return undefined;
    }
    if (isNil(newDailyBudget) || newDailyBudget === 0) {
      return undefined;
    }
    return validateCampaignDailyBudget({
      dailyTargetBudget: newDailyBudget,
      budget: newBudget
    });
  }, [dailyTargetBudget, newDailyBudget, newBudget]);

  const {
    loading,
    callAPIs
  } = useCallAPI();

  const [alertContent, setAlertContent] = useState<string | ReactElement | ReactElement[]>();

  const dismissAlertWindow = useCallback(() => {
    setAlertContent(undefined);
  }, []);

  const dailyBudgetState = useMemo(() => {
    return campaignManager.getCampaignDailyBudgetState(
      newBudget,
      newDailyBudget,
      startDate,
      endDate
    );
  }, [newBudget, newDailyBudget, startDate, endDate]);

  const dailyBudgetTips = useMemo(() => {
    switch (dailyBudgetState) {
      case DAILY_BUDGET_STATE.OVER_BUDGET:
        return (
          <Trans i18nKey='campaign.descriptions.overDailyBudget'>
            ...<span className='text-danger'>...</span>...
          </Trans>
        );
      case DAILY_BUDGET_STATE.UNDER_BUDGET:
        return (
          <Trans i18nKey='campaign.descriptions.underDailyBudget'>
            ...<span className='text-danger'>...</span>...
          </Trans>
        );
      case DAILY_BUDGET_STATE.MEET_BUDGET:
        return (
          <Trans i18nKey='campaign.descriptions.meetDailyBudget'>
            ...<span className='text-dark'>...</span>...
          </Trans>
        );
      case DAILY_BUDGET_STATE.DEFAULT:
      default:
        return;
    }
  }, [dailyBudgetState]);

  const checkIsUnderBudget = useCallback(() => {
    if (needShowDailyBudgetAlert && dailyBudgetState === DAILY_BUDGET_STATE.UNDER_BUDGET) {
      setAlertContent(
        <Trans i18nKey='campaign.descriptions.underDailyBudget'>
          ...<span className='text-danger'>...</span>...
        </Trans>
      );
    }
  }, [needShowDailyBudgetAlert, dailyBudgetState]);

  const renderBudgetInput = useCallback(() => {
    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setNewBudget(+e.target.value);
      setNeedShowDailyBudgetAlert(true);
    };
    const budgetBalance = l1Object.budgetBalance;
    const totalBudget = budgetBalance + budget;
    const remainBudget = round(Number(totalBudget) - newBudget, 2);
    return (
      <InputGroupField
        label={i18n.t<string>('campaignInfo.labels.totalBudget')}
        prefix={order.currency}
        name={'budget'}
        type='number'
        value={newBudget.toString()}
        min={0}
        autoComplete={'off'}
        onBlur={checkIsUnderBudget}
        hint={
          <Trans i18nKey='campaign.descriptions.remainBudget'>
            Remain budget <span className='text-dark'>{{ currency: order.currency, remainBudget }}</span>
          </Trans>
        }
        fieldContentWidth={400}
        onChange={onChange}
        error={newBudgetError}
      />
    );
  }, [
    order.currency,
    l1Object.budgetBalance,
    budget,
    newBudget,
    newBudgetError,
    checkIsUnderBudget
  ]);

  const isDistributionSchedule: boolean =
      isNil(dailyTargetBudget) || dailyTargetBudget === 0;

  const renderDailyBudgetInput = useCallback(() => {
    if (isDistributionSchedule) {
      return <div />;
    }
    const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setNewDailyBudget(+e.target.value);
      setNeedShowDailyBudgetAlert(true);
    };
    return (
      <CustomField
        label={i18n.t<string>('campaignInfo.labels.dailyBudgetPlan')}
        name='dailyBudgetPlan'
      >
        <>
          <LabelField
            value={i18n.t<string>('campaignInfo.labels.daily')}
            formGroupClassName={styles.marginBottom7}
          />
          <InputGroupField
            prefix={order.currency}
            name='dailyTargetBudget'
            hint={dailyBudgetTips}
            type='number'
            min={0}
            autoComplete='off'
            fieldContentWidth={400}
            value={newDailyBudget?.toString() || ''}
            onChange={onChange}
            onBlur={checkIsUnderBudget}
            formGroupClassName={styles.marginBottom0}
            error={newDailyBudgetError}
          />
        </>
      </CustomField>
    );
  }, [
    order.currency,
    newDailyBudget,
    dailyBudgetTips,
    newDailyBudgetError,
    isDistributionSchedule,
    checkIsUnderBudget
  ]);

  const modify = useCallback(() => {
    if (newBudget === budget && newDailyBudget === dailyTargetBudget) {
      onClose(false);
      return;
    }
    callAPIs(
      [
        () =>
          campaignManager.editCampaignBudget(
            l1Object.l1ObjectId,
            id,
            newBudget,
            newDailyBudget
          )
      ],
      () => {
        onClose(true);
      }
    );
  }, [
    id,
    l1Object.l1ObjectId,
    budget,
    newBudget,
    dailyTargetBudget,
    newDailyBudget,
    onClose,
    callAPIs
  ]);

  const onConfirmButtonClick = useCallback(() => {
    setShowFinalCheckModal(true);
  }, []);

  const onCloseFinalCheckModal = useCallback(() => {
    setShowFinalCheckModal(false);
  }, []);

  const leaveModifyBudgetModal = useCallback(() => {
    onClose(false);
  }, [onClose]);

  const budgetChanged = newBudget !== budget;
  const dailyBudgetChanged =
    !isDistributionSchedule && newDailyBudget !== dailyTargetBudget;

  const renderFinalCheckContent = useCallback(() => {
    const budgetLabel = formatPriceWithCurrency(order.currency, newBudget);
    const dailyBudgetLabel = formatPriceWithCurrency(
      order.currency,
      newDailyBudget || 0
    );
    if (budgetChanged && dailyBudgetChanged) {
      return (
        <Trans
          i18nKey='modifyBudget.labels.confirmModifyAllKindBudget'
          values={{ budget: budgetLabel, dailyBudget: dailyBudgetLabel }}
          components={{ 1: <span className={styles.danger} /> }}
        />
      );
    }
    if (budgetChanged) {
      return (
        <Trans
          i18nKey='modifyBudget.labels.confirmModifyBudget'
          values={{ budget: budgetLabel }}
          components={{ 1: <span className={styles.danger} /> }}
        />
      );
    }

    if (dailyBudgetChanged) {
      return (
        <Trans
          i18nKey='modifyBudget.labels.confirmModifyDailyBudget'
          values={{ dailyBudget: dailyBudgetLabel }}
          components={{ 1: <span className={styles.danger} /> }}
        />
      );
    }
    return '';
  }, [
    budgetChanged,
    newBudget,
    dailyBudgetChanged,
    newDailyBudget,
    order.currency
  ]);

  return (
    <Modal
      title=''
      secondaryButton={{
        title: i18n.t<string>('common.buttons.cancel'),
        callback: leaveModifyBudgetModal
      }}
      primaryButton={{
        title: i18n.t<string>('common.buttons.confirm'),
        callback: onConfirmButtonClick,
        disabled:
          !!newBudgetError ||
          !!newDailyBudgetError ||
          (!budgetChanged && !dailyBudgetChanged)
      }}
      dismiss={leaveModifyBudgetModal}
      className={styles.modal}
      backdrop='static'
    >
      {loading && <LoadingIndicator />}
      {renderBudgetInput()}
      {renderDailyBudgetInput()}
      {alertContent && (
        <AlertDialog
          title={i18n.t<string>('common.warning')}
          message={alertContent}
          danger={false}
          dismiss={dismissAlertWindow}
        />
      )}
      {showFinalCheckModal && (
        <Modal
          title={i18n.t<string>('modifyBudget.titles.confirmModifyBudget')}
          dismiss={onCloseFinalCheckModal}
          className={styles.confirmModal}
          primaryButton={{
            title: i18n.t<string>('common.buttons.confirm'),
            callback: modify
          }}
          secondaryButton={{
            title: i18n.t<string>('common.buttons.cancel'),
            callback: onCloseFinalCheckModal
          }}
        >
          {renderFinalCheckContent()}
        </Modal>
      )}
    </Modal>
  );
};
